import {
  ExportController_profitAndLossCsv_v1,
  type OutletProfitAndLossReportResource,
  type OutletProfitAndLossResource,
  type OutletResource,
  OutletsController_list_v1,
  ProfitAndLossController_outlet_v1,
  ProfitAndLossController_park_v1,
  type ProfitAndLossReportResource,
  type ProfitAndLossResource,
  type ReportPeriodResource,
} from '@repo/api-client';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import { Label } from '@repo/ui/components/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import { IconDownload, IconLoader2 } from '@tabler/icons-react';
import { type ReactNode, useEffect, useId, useMemo, useState } from 'react';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { isForbidden, isNotFound, messageForFailure } from '@/api/failure';
import { systemClock } from '@/lib/clock';
import { formatPaise } from '@/lib/money';

import { DayPicker } from '../back-office/DayPicker';
import { EmptyState, LoadFailed, Screen, ScreenFact, ScreenTable } from '../back-office/Screen';
import { isRetired } from '../outlets/outlet-kinds';
import { formatBusinessDate, fromCalendarDate, toCalendarDate } from '../tickets/business-date';
import { saveDownloadedFile } from './cash-book-export';
import { costLines } from './profit-and-loss-cost';
import { PROFIT_AND_LOSS_DEFAULT_FILENAME } from './profit-and-loss-export';
import {
  currentBusinessDate,
  defaultPeriod,
  periodProblem,
  type ReportingPeriod,
} from './reporting-period';

/**
 * What the park took, what the goods cost, what margin that left and what is
 * finally left: `GET /v1/reports/profit-and-loss`, with
 * `GET /v1/reports/outlets/{outletId}/profit-and-loss` for one outlet and
 * `GET /v1/reports/profit-and-loss.csv` beside them.
 *
 * ## This is the most sensitive read in the system, and it is owner-only
 *
 * All three routes declare `@RequireRole('OWNER')` and **a manager is refused
 * `403 AUTH_FORBIDDEN` at the routing layer**, before the handler runs. There is
 * no partial response and no field-level filtering: an owner gets everything or
 * a manager gets nothing, and that is the whole of the role split.
 *
 * The rule behind the split is exactly cost and margin. `export.controller.ts`
 * says so in those words — `profit-and-loss.csv` is owner-only *because* it
 * carries both, and `cash-book.csv` beside it is outlet-scoped and carries
 * neither. `OutletTradingSummaryResource` is the same nine figures with
 * `costOfGoods` **absent at the type level rather than filtered on the way out**,
 * which is what a manager reads instead; that is FE052, on `/trading-summary`,
 * and this screen is not a role-flagged variant of it.
 *
 * **Do not infer this screen's guard from that one.** The two routes are not
 * symmetric and the park and outlet reads here are not guaranteed to match each
 * other either — each entry in `route-table.tsx` states what its own route
 * declares. `route-audit.ts` throws on a route that declared *nothing*; a route
 * that declared the *wrong role* audits perfectly clean and is simply wrong.
 *
 * The screen still handles `AUTH_FORBIDDEN` arriving. `role` is restored from
 * `sessionStorage` and is attacker-editable, so hiding the navigation link is
 * courtesy and the server is what refuses.
 *
 * ## Two routes, and why the outlet one is not a filter over the park read
 *
 * `ProfitAndLossReportResource` carries the park **and** every outlet the caller
 * reaches, so narrowing to one outlet by filtering that array is available and
 * is deliberately not what happens. Three reasons, in order of weight:
 *
 * 1. It would fetch **every outlet's cost position** over the wire to look at
 *    one, on the one report where that is the disclosure worth minimising.
 * 2. `outletName` is not unique and never has been — two live outlets may share
 *    a name, and a period spanning a retire-and-recreate publishes two rows for
 *    what a person calls one shop. The route takes the id and answers for it;
 *    a client-side filter would have to guess.
 * 3. The route answers `404` for an outlet that does not exist and for one
 *    outside the caller's reach, identically and on purpose (FR-724). A filter
 *    over an array cannot tell "not in this period's report" from "not a thing",
 *    and would render an empty statement for both.
 *
 * ## Nothing here is computed
 *
 * `netPaise` and `grossMarginPaise` are derived by the server on every read and
 * stored nowhere (R-701), and `docs/ui-conventions.md` is explicit that the
 * client does not compute totals. So the figures are rendered as sent. If the
 * terms on screen ever fail to add up to the net beside them, the honest thing
 * is that the discrepancy is *visible* — that is a defect to report to the API,
 * and a screen that quietly agreed with its own sum would be the one place it
 * could never be found.
 *
 * The two figures are also **different questions and are never added**. The net
 * is revenue less expenses; the margin is goods revenue less what those goods
 * cost, with no admission in it at all, because a ticket has no cost of goods
 * and including it would inflate the margin by the whole gate. On the document's
 * own example the net is ₹5,720.00 and the margin is ₹1,810.00.
 *
 * Refunds are a **disclosure and not a term**: `goodsRevenuePaise` arrives
 * already net of the refunds counted in the same period, so they sit below the
 * net rather than above it and the note beside them says they have already been
 * taken off. `blossom-rest-api#335` was a worked example in the spec that
 * subtracted them inside the margin; it is corrected and merged, and no
 * arithmetic on this screen was copied from it because this screen does none.
 *
 * ## Cost of goods has three answers, not two
 *
 * `costOfGoods.applicability` distinguishes "sold goods that cost ₹0.00" from
 * "sold no goods", and collapsing the second into the first is a claim about the
 * park's buying position that nobody made. `profit-and-loss-cost.ts` owns that
 * branch and the reasoning; this file renders its answer.
 *
 * ## The export is the park's, always
 *
 * `GET /v1/reports/profit-and-loss.csv` takes `from` and `to` and **no outlet**.
 * So the button exports the whole park even while the statement is narrowed to
 * one outlet, and it says so rather than sending a parameter the route does not
 * declare. It goes through `ApiClient.download` and never an anchor —
 * `profit-and-loss-export.ts` and `cash-book-export.ts` set out why at length.
 */

/** Every state the statement can be in. One value, so two cannot coexist. */
type ReportState =
  /** The period is half-chosen or refused, so there is nothing to ask for. */
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'park'; readonly report: ProfitAndLossReportResource }
  | { readonly kind: 'outlet'; readonly report: OutletProfitAndLossReportResource }
  | { readonly kind: 'failed'; readonly error: unknown };

/** Where the export has got to. The failure is worded, so it is carried. */
type ExportState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'running' }
  | { readonly kind: 'failed'; readonly error: unknown };

/**
 * The default scope, and what the screen opens on.
 *
 * Not `''`, which Base UI clears a select to — and unlike the trading summary
 * there *is* a park-wide answer here, so waiting for a choice would be a blank
 * screen where a report belongs.
 */
const WHOLE_PARK = 'whole-park';

export function ProfitAndLoss() {
  const client = useApiClient();

  // `WHOLE_PARK` or an outlet id. One value rather than a pair, so "the park,
  // but also an outlet id left over from last time" is not a state.
  const [scope, setScope] = useState<string>(WHOLE_PARK);

  // The period the report is read over, and the period the pickers show. They
  // are the same until somebody starts changing it: a half-cleared range is not
  // a request, so the last complete one stays on screen while a new one is
  // chosen.
  const [period, setPeriod] = useState<ReportingPeriod>(() => defaultPeriod(systemClock));

  const [state, setState] = useState<ReportState>({ kind: 'loading' });

  // A counter rather than a boolean, so two retries in a row are two distinct
  // values and the second one still fires.
  const [reloads, setReloads] = useState(0);

  const [outlets, setOutlets] = useState<readonly OutletResource[]>([]);

  const [exporting, setExporting] = useState<ExportState>({ kind: 'idle' });

  const problem = periodProblem(period, systemClock);

  const complete = period.from !== '' && period.to !== '' && problem === null;

  // The park's own day, in Asia/Kolkata — not the device's. Read once per render
  // rather than held in state: a screen left open across midnight should bound
  // itself at the new day, not at the day it opened on.
  const today = currentBusinessDate(systemClock);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    // Read whole: the outlets are bounded by the park, which is the case
    // `docs/ui-conventions.md` gives `collectPages` for. Retired ones included —
    // this is a report over a past period, and an outlet retired since it still
    // traded during it.
    collectPages<OutletResource>((offset, limit) =>
      client.request(OutletsController_list_v1, {
        query: { offset, limit, includeRetired: true },
        signal: controller.signal,
      }),
    )
      .then((all) => {
        if (live) setOutlets(all);
      })
      .catch(() => {
        // The picker is the only casualty. The park report does not need it, and
        // it is what this screen opens on.
        if (live) setOutlets([]);
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client]);

  useEffect(() => {
    // A half-filled or refused period is not a request: the notice under the
    // pickers is already saying so, and sending it would earn a 422 for
    // something the screen can see.
    if (!complete) {
      setState({ kind: 'idle' });

      return;
    }

    const controller = new AbortController();
    let live = true;

    setState({ kind: 'loading' });

    const query = { from: period.from, to: period.to };

    const read =
      scope === WHOLE_PARK
        ? client
            .request(ProfitAndLossController_park_v1, { query, signal: controller.signal })
            .then((report): ReportState => ({ kind: 'park', report }))
        : client
            .request(ProfitAndLossController_outlet_v1, {
              path: { outletId: scope },
              query,
              signal: controller.signal,
            })
            .then((report): ReportState => ({ kind: 'outlet', report }));

    read
      .then((next) => {
        if (live) setState(next);
      })
      .catch((error: unknown) => {
        // An abort is this screen's own doing — a filter changed, or it
        // unmounted — and is not a failure to report.
        if (live && !(error instanceof DOMException && error.name === 'AbortError')) {
          setState({ kind: 'failed', error });
        }
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, scope, period.from, period.to, complete, reloads]);

  /**
   * The CSV, over the period the statement is already showing.
   *
   * **The whole park's, whatever the statement is narrowed to.** The route takes
   * `from` and `to` and nothing else, and a screen that sent an `outletId` it
   * does not declare would be inventing a filter the file would not honour.
   */
  const runExport = () => {
    if (!complete || exporting.kind === 'running') return;

    setExporting({ kind: 'running' });

    client
      .download(ExportController_profitAndLossCsv_v1, {
        query: { from: period.from, to: period.to },
      })
      .then((file) => {
        saveDownloadedFile(file, PROFIT_AND_LOSS_DEFAULT_FILENAME);
        setExporting({ kind: 'idle' });
      })
      .catch((error: unknown) => {
        setExporting({ kind: 'failed', error });
      });
  };

  // Base UI resolves what the trigger shows from `items` and not from the
  // rendered options, so a picker without it shows a bare UUID where an outlet
  // name belongs. Derived from the same rows the options render.
  const scopeItems = useMemo(
    () => [
      { label: 'The whole park', value: WHOLE_PARK },
      ...outlets.map((outlet) => ({ label: outlet.name, value: outlet.id })),
    ],
    [outlets],
  );

  const chosenOutlet = outlets.find((outlet) => outlet.id === scope);

  return (
    <Screen
      title="Profit and loss"
      description="What the park took over a period, what the goods it sold cost, the margin that left, and what is finally left after expenses. The owner's report — a manager reads the trading summary, which carries no cost and no margin."
      action={
        <Button
          onClick={runExport}
          // Nothing to export while the period is refused, and the API would
          // answer the same 422 the notice under the pickers is already giving.
          disabled={!complete || exporting.kind === 'running'}
        >
          {exporting.kind === 'running' ? (
            <IconLoader2 data-icon="inline-start" className="animate-spin" />
          ) : (
            <IconDownload data-icon="inline-start" />
          )}
          {exporting.kind === 'running' ? 'Exporting…' : 'Export CSV'}
        </Button>
      }
      aside={
        <>
          <div className="space-y-2">
            <Label htmlFor="profit-and-loss-scope" className="text-sm text-muted-foreground">
              Report on
            </Label>
            <Select
              value={scope}
              items={scopeItems}
              onValueChange={(next) => {
                // Base UI types the value as nullable because a select may be
                // cleared. The park is what this screen falls back to, and it is
                // a real answer rather than "nothing chosen".
                setScope(next ?? WHOLE_PARK);
              }}
            >
              <SelectTrigger
                id="profit-and-loss-scope"
                aria-label="Report on"
                className="h-10 w-full bg-card font-normal"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value={WHOLE_PARK}>The whole park</SelectItem>
                  {outlets.map((outlet) => (
                    <SelectItem key={outlet.id} value={outlet.id}>
                      {outlet.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="profit-and-loss-from" className="text-sm text-muted-foreground">
              From
            </Label>
            <DayPicker
              id="profit-and-loss-from"
              value={period.from}
              emptyLabel="Choose a day"
              format={formatBusinessDate}
              fromDate={fromCalendarDate}
              toDate={toCalendarDate}
              maxDay={today}
              onValueChange={(next) => {
                setPeriod((held) => ({ ...held, from: next }));
              }}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="profit-and-loss-to" className="text-sm text-muted-foreground">
              To
            </Label>
            <DayPicker
              id="profit-and-loss-to"
              value={period.to}
              emptyLabel="Choose a day"
              format={formatBusinessDate}
              fromDate={fromCalendarDate}
              toDate={toCalendarDate}
              // The route refuses a period ending after the park's current
              // trading day, so a day past it is not offered. The notice below
              // still stands — see `maxDay` on `DayPicker` for why both.
              maxDay={today}
              onValueChange={(next) => {
                setPeriod((held) => ({ ...held, to: next }));
              }}
            />
          </div>

          {/* Under the pickers rather than above the statement: it is about the
              two controls immediately above it, and it is not a failed read. */}
          {problem !== null && (
            <p role="status" className="text-sm text-destructive">
              {problem}
            </p>
          )}

          <ScreenFact label="Period">
            {period.from === '' || period.to === '' ? (
              'Choose both days.'
            ) : (
              <>
                {formatBusinessDate(period.from)} to {formatBusinessDate(period.to)}
                <br />
                <span className="text-muted-foreground">
                  Both days included, by the trading day each record fell in.
                </span>
              </>
            )}
          </ScreenFact>

          {chosenOutlet !== undefined && (
            <ScreenFact label="Outlet">
              <span className="font-mono numeric">{chosenOutlet.code}</span> — {chosenOutlet.name}
              {isRetired(chosenOutlet) && (
                <>
                  {' '}
                  {/* `outline` and not `destructive`: retiring an outlet is an
                      ordinary end to an ordinary life, and it still traded. */}
                  <Badge variant="outline">Retired</Badge>
                </>
              )}
            </ScreenFact>
          )}

          <ScreenFact label="Export">
            <span className="text-muted-foreground">
              The export is the whole park&rsquo;s, over the period above. This route takes no
              outlet.
            </span>
          </ScreenFact>

          {/* A control-level failure goes where the control is, not in a toast:
              toasts vanish and somebody who looked away cannot get it back. */}
          {exporting.kind === 'failed' && (
            <p role="status" className="text-sm text-destructive">
              The export could not be made. {messageForFailure(exporting.error)}
            </p>
          )}
        </>
      }
    >
      {state.kind === 'idle' && (
        <Notice>
          <EmptyState
            title="Choose the period to report on"
            description="A profit and loss is read over two days that bound it. Choose both, and a period that ends no later than the park's current trading day."
          />
        </Notice>
      )}

      {state.kind === 'loading' && <StatementSkeleton />}

      {state.kind === 'failed' && (
        <Notice>
          {isForbidden(state.error) ? (
            <EmptyState
              title="This account cannot read the profit and loss"
              description="What the goods cost and the margin on them are the owner's alone, and the server refuses everybody else before the report is built. If you are the owner, sign out and back in — the role this browser is holding may be out of date."
            />
          ) : isNotFound(state.error) ? (
            // The route answers 404 for an outlet that does not exist and for one
            // outside the caller's reach, identically and on purpose. Said as the
            // one sentence that covers both, rather than as `RESOURCE_NOT_FOUND`'s
            // "go back and refresh the list" — there is no list here, and guessing
            // which of the two it was is precisely the disclosure the API declined
            // to make.
            <EmptyState
              title="That outlet is not one this account can report on"
              description="It may have been removed, or it may be outside what this account reaches. Choose another outlet, or report on the whole park."
            />
          ) : (
            <LoadFailed
              error={state.error}
              onRetry={() => {
                setReloads((count) => count + 1);
              }}
              title="This profit and loss could not be read"
            />
          )}
        </Notice>
      )}

      {state.kind === 'park' && (
        <>
          <Statement
            name="The whole park"
            figures={state.report.park}
            period={state.report.period}
            subject="the park"
          />
          <Outlets outlets={state.report.outlets} />
        </>
      )}

      {state.kind === 'outlet' && (
        <Statement
          name={state.report.outlet.outletName}
          figures={state.report.outlet}
          period={state.report.period}
          subject="this outlet"
        />
      )}
    </Screen>
  );
}

/**
 * The statement: what came in and went out, then what the goods cost, then the
 * refund disclosure that is deliberately neither.
 *
 * `figures` is typed as the park's resource and the outlet's is assignable to it
 * — the outlet adds `outletId` and `outletName` and changes nothing else. So one
 * component renders both, and a field appearing on one and not the other would
 * fail to compile here rather than render blank on one of two screens.
 *
 * Named by its subject, so the region a reader lands on says whose figures these
 * are without their having to look back at the rail.
 */
function Statement({
  name,
  figures,
  period,
  subject,
}: {
  name: string;
  figures: ProfitAndLossResource;
  period: ReportPeriodResource;
  /** "the park" or "this outlet" — the wording for the no-trading notice. */
  subject: string;
}) {
  const cost = costLines(figures.costOfGoods);

  return (
    <Panel
      name={name}
      description={`${formatBusinessDate(period.from)} to ${formatBusinessDate(period.to)}, both days included.`}
    >
      {/* `hadTrading` is an answer and not a refusal, and the figures stay
          beside it: somewhere that was shut but paid for a delivery still has an
          expense to show, and hiding it behind "nothing happened" would
          contradict the expense log. */}
      {!period.hadTrading && (
        <p role="status" className="mb-5 text-sm text-muted-foreground">
          Nothing traded in {subject} between these two days. The figures below are the answer for
          that period, not a failure to read it.
        </p>
      )}

      <dl>
        {/* Two revenue lines and no sum of them: only goods carry a cost of
            goods sold, and that distinction is what the margin below is built
            on. */}
        <Figure term="Goods sold">{figures.goodsRevenuePaise}</Figure>
        <Figure term="Admission">{figures.admissionRevenuePaise}</Figure>
        <Figure term="Admission entered by hand">{figures.assertedAdmissionRevenuePaise}</Figure>
        <Figure term="Expenses">{figures.expensesPaise}</Figure>
        <Figure term="Net for the period" strong>
          {figures.netPaise}
        </Figure>
      </dl>

      <div className="mt-4 space-y-2 text-sm text-muted-foreground">
        <p>
          {figures.assertedAdmissionCount === 0
            ? 'No admission was entered by hand in this period.'
            : `Admission entered by hand covers ${String(figures.assertedAdmissionCount)} ${figures.assertedAdmissionCount === 1 ? 'admission' : 'admissions'} put into the books by a person after a till could not send them. It is not inside the admission line above, and it is in the net.`}
        </p>
        <p>The net is the server&rsquo;s arithmetic, taken as sent.</p>
      </div>

      {/* Its own band, because the margin is not a continuation of the running
          figures above — it is goods revenue less what those goods cost, and it
          has no admission and no expenses in it. Setting it below the net inside
          one column of figures would invite reading the two as one sum. */}
      <div className="mt-6 border-t border-border pt-4">
        {cost.kind === 'figures' && (
          <>
            <dl>
              <Figure term="Cost of goods sold">{cost.costOfGoodsSoldPaise}</Figure>
              <Figure term="Gross margin" strong>
                {cost.grossMarginPaise}
              </Figure>
            </dl>
            <p className="mt-2 text-sm text-muted-foreground">
              Gross margin is goods sold less what those goods cost. Admission is not in it — a
              ticket has no cost of goods — so it and the net above answer different questions and
              are not added to one another.
            </p>
          </>
        )}

        {cost.kind === 'no-goods' && (
          <p className="text-sm text-muted-foreground">
            No goods were sold in this period, so there is no cost of goods and no margin. That is
            not a cost of zero: zero would say the goods sold cost the park nothing.
          </p>
        )}

        {cost.kind === 'inconsistent' && (
          <p role="status" className="text-sm text-destructive">
            The cost of goods on this report contradicts what it says about itself, so it is not
            shown rather than being guessed at. Report it — nothing else on this statement is
            affected.
          </p>
        )}
      </div>

      {/* Below the net and outside the running figures, because it is a
          disclosure rather than a term: `goodsRevenuePaise` has already had it
          removed. */}
      <div className="mt-6 border-t border-border pt-4">
        <dl>
          <Figure term="Handed back">{figures.refundsPaise}</Figure>
        </dl>
        <p className="mt-2 text-sm text-muted-foreground">
          Refunds made in this period, counted in the period they happened in rather than the one
          the sale did. It is already out of goods sold above, and out of the cost and the margin
          with it, so it is not taken off a second time.
        </p>
      </div>
    </Panel>
  );
}

/**
 * Every outlet under the park's own figures, as the margin comparison the park
 * statement above cannot make.
 *
 * Five columns and not nine: the full statement for one outlet is a choice in
 * the rail away, and this table exists to answer "which outlet is earning its
 * shelf space", which is goods, cost, margin and net.
 *
 * **Keyed on `outletId`.** `outletName` is not unique and never has been — two
 * live outlets may share a name, and retiring an outlet frees its code for
 * reuse, so a period spanning a retire-and-recreate publishes two rows for what
 * a person calls one shop. A row keyed on the name would drop one of the two and
 * the park total above would then disagree with the outlets under it by whatever
 * that shop took.
 *
 * An outlet that did not trade is present with zeroes rather than absent, and so
 * is one retired since the period — a financial period is not a current-state
 * list.
 */
function Outlets({ outlets }: { outlets: readonly OutletProfitAndLossResource[] }) {
  const anyWithoutGoods = outlets.some(
    (outlet) => costLines(outlet.costOfGoods).kind === 'no-goods',
  );

  return (
    <>
      <ScreenTable label="Outlets">
        <TableHeader>
          <TableRow>
            <TableHead>Outlet</TableHead>
            <TableHead className="text-right">Goods sold</TableHead>
            <TableHead className="text-right">Cost of goods</TableHead>
            <TableHead className="text-right">Gross margin</TableHead>
            <TableHead className="text-right">Net</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {outlets.map((outlet) => {
            const cost = costLines(outlet.costOfGoods);

            return (
              <TableRow key={outlet.outletId}>
                <TableCell>{outlet.outletName}</TableCell>
                <Amount>{outlet.goodsRevenuePaise}</Amount>
                <CostCell>
                  {cost.kind === 'figures' ? cost.costOfGoodsSoldPaise : cost.kind}
                </CostCell>
                <CostCell>{cost.kind === 'figures' ? cost.grossMarginPaise : cost.kind}</CostCell>
                <Amount>{outlet.netPaise}</Amount>
              </TableRow>
            );
          })}
        </TableBody>
      </ScreenTable>

      <p className="text-sm text-muted-foreground">
        The park&rsquo;s figures above are these outlets summed, not a separate question.
        {anyWithoutGoods
          ? ' A dash in the cost and margin columns means that outlet sold no goods in this period — it is not a cost of zero.'
          : ''}{' '}
        Two rows may carry the same name: retiring an outlet frees its code for reuse, and a period
        that spans a retire and a recreate reports both.
      </p>
    </>
  );
}

const EMPTY_COST = '—';

/** A right-aligned amount cell, in the column treatment money gets everywhere. */
function Amount({ children }: { children: number }) {
  return <TableCell className="text-right font-mono numeric">{formatPaise(children)}</TableCell>;
}

/**
 * A cost or margin cell, which has three answers rather than one.
 *
 * A dash for "sold no goods" and a word for the contradiction — never `₹0.00`
 * for either, because that is a claim the API did not make. The footnote under
 * the table says what the dash means, so the column is not a rebus.
 */
function CostCell({ children }: { children: number | 'no-goods' | 'inconsistent' }) {
  if (typeof children === 'number') return <Amount>{children}</Amount>;

  return (
    <TableCell className="text-right text-muted-foreground">
      {children === 'no-goods' ? EMPTY_COST : 'Not shown'}
    </TableCell>
  );
}

/**
 * One line of a statement: a term and its amount.
 *
 * Amounts right-align and carry `numeric`, per `docs/ui-conventions.md` — Geist's
 * default figures are proportional, and reading down a column of money is
 * exactly what this screen is for.
 *
 * A definition list and not a table: these are labelled values rather than rows
 * of records, and `<dt>`/`<dd>` is what pairs a label with its value for
 * somebody reading with a screen reader.
 */
function Figure({
  term,
  children,
  strong = false,
}: {
  term: string;
  children: number;
  strong?: boolean;
}) {
  return (
    <div
      className={`flex items-baseline justify-between gap-4 py-2 ${strong ? 'mt-1 border-t border-border pt-3' : ''}`}
    >
      <dt className={strong ? 'text-foreground' : 'text-muted-foreground'}>{term}</dt>
      <dd
        className={`text-right font-mono numeric ${strong ? 'font-medium text-foreground' : 'text-foreground'}`}
      >
        {formatPaise(children)}
      </dd>
    </div>
  );
}

/**
 * A card carrying one of the states where there is no statement to show.
 *
 * No heading, because `EmptyState` and `LoadFailed` each supply their own and a
 * panel title above one of those says the same thing twice. The centring and the
 * measure are `TableNotice`'s, which is where every list screen in the app puts
 * these two.
 */
function Notice({ children }: { children: ReactNode }) {
  return (
    // **No box.** The panel is the surface, so the old card was a card inside a
    // card. What it was actually doing was centring a measure in an otherwise
    // empty field, and that is what `TableNotice` does on every list screen —
    // it takes the records band's height and sets the block in the middle of
    // it. This does the same with `flex-1`, so a screen with no period chosen
    // reads the way a list with no rows does rather than stranding one sentence
    // against the toolbar with a thousand pixels of nothing under it.
    <div className="flex min-h-0 flex-1 items-center justify-center py-14">
      <div className="mx-auto max-w-md space-y-3 text-center">{children}</div>
    </div>
  );
}

/**
 * A band of the page, named so the statement is a landmark a reader can jump to.
 *
 * The same composition `DayClose` and `TradingSummary` use. That is now the third
 * screen wanting it, which is well past the moment `docs/ui-conventions.md` says
 * to promote it to `back-office/Screen.tsx` beside `ScreenFact` and `EmptyState`
 * — that move touches two other epics' screens while their tickets are in
 * flight, so it is named in the PR body rather than taken here.
 */
function Panel({
  name,
  description,
  children,
}: {
  name: string;
  description?: string;
  children: ReactNode;
}) {
  const headingId = useId();

  return (
    // **A ruled region, not a card.** A heading and a hairline: the panel under
    // it is the surface, and a bordered box on it is a card inside a card. The
    // gutter goes with the border — the records band already sets the field's
    // extent, so the figures now right-align to the field's own edge, which is
    // what `docs/ui-conventions.md` asks of a column of money.
    <section aria-labelledby={headingId} className="pt-2">
      <header className="border-b border-border pb-3">
        <h2 id={headingId} className="font-medium text-foreground">
          {name}
        </h2>
        {description !== undefined && (
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        )}
      </header>
      <div className="pt-5">{children}</div>
    </section>
  );
}

/**
 * The statement's shape while it is in flight.
 *
 * A skeleton and not a spinner, at roughly the height the loaded panel settles
 * at, so the page does not jump when the figures land. `aria-hidden` because
 * there is nothing here to read — and **not** a second `ScreenTable` named
 * "Outlets": a landmark that exists while the data does not is one a reader can
 * jump into and find empty.
 */
function StatementSkeleton() {
  return (
    <>
      <div aria-hidden className="h-[30rem] animate-pulse rounded-2xl bg-muted" />
      <div aria-hidden className="h-40 animate-pulse rounded-2xl bg-muted" />
    </>
  );
}
